import React, { createContext, useContext, useEffect, useState } from 'react';
import { noop } from 'lodash';
import { Checkbox } from 'syngenta-digital-cropwise-react-ui-kit';
import ArrowRight from '@material-ui/icons/ArrowRight';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import Add from '@material-ui/icons/Add';
import Remove from '@material-ui/icons/Remove';
import './index.css';

/*
 * The nested, expandable row groups the Property trees are built from (the
 * mass assigner, the change summary and the import picker). The UI Kit has no
 * equivalent: its Table expands data rows rather than composed groups, so
 * this keeps grow-ui Table's composition and markup, with the kit Checkbox.
 */

type OnSelect = (event: React.ChangeEvent<HTMLInputElement>) => void;

// What a row takes from the groups around it, one value per context so each
// provider passes a plain value rather than a new object every render.
// Nesting depth: a group's header row sits at the group's depth and its
// members one deeper. The first cell is indented by it (see index.css).
const LevelContext = createContext(0);
// Inside an open group: rows show the group's left strip.
const InGroupContext = createContext(false);
const SelectableContext = createContext<boolean | undefined>(false);
const OnSelectContext = createContext<OnSelect | undefined>(undefined);

type SelectionProps = {
  selectable?: boolean;
  selected?: boolean;
  indeterminate?: boolean;
  onSelect?: OnSelect;
};

type TableProps = {
  children: React.ReactNode;
  selectable?: boolean;
  onSelect?: OnSelect;
};

const GroupTable = ({ children, selectable = false, onSelect }: TableProps) => (
  <SelectableContext.Provider value={selectable}>
    <OnSelectContext.Provider value={onSelect}>
      <div className="cwf-kit-group-table">{children}</div>
    </OnSelectContext.Provider>
  </SelectableContext.Provider>
);

type RowProps = SelectionProps & {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  // Set by RowGroup on its header row: the open strip and expand toggle.
  groupLead?: React.ReactNode;
};

const RowCheckbox = ({
  selected,
  indeterminate,
  onSelect
}: Pick<SelectionProps, 'selected' | 'indeterminate' | 'onSelect'>) => (
  <div
    className="cwf-kit-group-table__checkbox"
    data-testid="group-table-row-checkbox"
  >
    <Checkbox
      checked={selected}
      indeterminate={indeterminate}
      onChange={event =>
        (onSelect ?? noop)(
          (event as unknown) as React.ChangeEvent<HTMLInputElement>
        )
      }
    />
  </div>
);

const Row = ({
  children,
  onClick,
  className = '',
  selectable,
  selected = false,
  indeterminate = false,
  onSelect,
  groupLead
}: RowProps) => {
  const inheritedSelectable = useContext(SelectableContext);
  const inheritedOnSelect = useContext(OnSelectContext);
  const level = useContext(LevelContext);
  const inGroup = useContext(InGroupContext);
  const isSelectable = selectable ?? inheritedSelectable ?? false;
  const handleSelect = onSelect ?? inheritedOnSelect;

  // The strip, toggle and checkbox go in front of the first child's own
  // content, whatever that child is (a Cell, or a plain element).
  const lead = (
    <>
      {inGroup && <span className="cwf-kit-group-table__strip" />}
      {groupLead}
      {isSelectable && (
        <RowCheckbox
          selected={selected}
          indeterminate={indeterminate}
          onSelect={handleSelect}
        />
      )}
    </>
  );
  const [first, ...rest] = React.Children.toArray(children);
  const firstChild = React.isValidElement<{ children?: React.ReactNode }>(first)
    ? React.cloneElement(
        first,
        undefined,
        <>
          {lead}
          {first.props.children}
        </>
      )
    : first;

  // A selectable row's checkbox is its tab stop (Space on it selects the row
  // through the click), so the row itself stays out of the tab order.
  const interaction = onClick
    ? {
        role: 'button',
        tabIndex: isSelectable ? -1 : 0,
        onClick,
        onKeyDown: (event: React.KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            onClick();
          }
        }
      }
    : {};

  return (
    <div
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...interaction}
      data-testid={
        isSelectable ? 'group-table-selectable-row' : 'group-table-row'
      }
      data-level={level}
      data-group-header={groupLead ? 'true' : undefined}
      className={`cwf-kit-group-table__row ${
        selected ? 'cwf-kit-group-table__row--selected' : ''
      } ${
        isSelectable ? 'cwf-kit-group-table__row--selectable' : ''
      } ${className}`}
    >
      {firstChild}
      {rest}
    </div>
  );
};

type CellProps = {
  children?: React.ReactNode;
  className?: string;
};

const Cell = ({ children, className = '' }: CellProps) => (
  <div className={`cwf-kit-group-table__cell ${className}`}>
    <div className="cwf-kit-group-table__cell-content">{children}</div>
  </div>
);

type RowGroupProps = SelectionProps & {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  autoOpen?: boolean;
  arrowIcon?: boolean;
  canOpen?: boolean;
};

const RowGroup = ({
  children,
  onClick,
  className = '',
  autoOpen = true,
  arrowIcon = false,
  canOpen = true,
  selectable,
  selected = false,
  indeterminate = false,
  onSelect
}: RowGroupProps) => {
  const [open, setOpen] = useState(autoOpen);
  const level = useContext(LevelContext);

  useEffect(() => {
    setOpen(autoOpen);
  }, [autoOpen]);

  const [header, ...members] = React.Children.toArray(children);
  const ClosedIcon = arrowIcon ? ArrowRight : Add;
  const OpenIcon = arrowIcon ? ArrowDropDown : Remove;
  const Icon = open ? OpenIcon : ClosedIcon;

  const toggle = canOpen ? (
    <div className="cwf-kit-group-table__toggle">
      <button
        type="button"
        aria-label={open ? 'Collapse' : 'Expand'}
        aria-expanded={open}
        data-testid="group-table-toggle"
        // In selectable groups the checkbox is the row's only tab stop.
        tabIndex={selectable ? -1 : undefined}
        className={`cwf-kit-group-table__toggle-button ${
          arrowIcon ? '' : 'cwf-kit-group-table__toggle-button--boxed'
        }`}
        onClick={event => {
          // The row itself selects on click; the toggle only expands.
          event.stopPropagation();
          event.preventDefault();
          setOpen(previous => !previous);
        }}
      >
        <Icon fontSize={arrowIcon ? 'small' : 'inherit'} />
      </button>
    </div>
  ) : (
    <div className="cwf-kit-group-table__toggle-spacer" />
  );

  const headerRow = React.isValidElement<RowProps>(header)
    ? React.cloneElement(header, {
        onClick,
        className,
        selectable,
        selected,
        indeterminate,
        onSelect,
        groupLead: (
          <>
            {open && <span className="cwf-kit-group-table__strip" />}
            {toggle}
          </>
        )
      })
    : header;

  // Each group's rows share one container, so a sticky header only sticks
  // while its own group is in view.
  return (
    <div className="cwf-kit-group-table__group">
      {headerRow}
      {open && (
        <LevelContext.Provider value={level + 1}>
          <InGroupContext.Provider value>
            <SelectableContext.Provider value={selectable}>
              <OnSelectContext.Provider value={onSelect}>
                {members}
              </OnSelectContext.Provider>
            </SelectableContext.Provider>
          </InGroupContext.Provider>
        </LevelContext.Provider>
      )}
    </div>
  );
};

GroupTable.Row = Row;
GroupTable.Cell = Cell;
GroupTable.RowGroup = RowGroup;

export default GroupTable;
